import { useMemo } from 'react';
import type { PinKind } from '@werft/contracts';
import { t } from '../../i18n/index.js';
import { newOpId } from '../../platform/api.js';
import { useOutboxEntries } from '../../platform/outbox.js';
import { keys, queryClient, usePins } from '../../platform/queries.js';
import { outbox } from '../../platform/sync.js';
import { toast } from '../../platform/toast.js';
import { savedNote } from '../../platform/undo.js';

/**
 * Audit F07: Seiten, Dateien und Themen an die Projektübersicht heften – gleichwertig mit angehefteten Aufgaben.
 * Über den Ausgangskorb; die sichtbare Wirkung (angeheftet ja/nein) als Overlay.
 */
const reload = (pid: string) => {
  void queryClient.invalidateQueries({ queryKey: keys.pins(pid) });
  void queryClient.invalidateQueries({ queryKey: keys.overview(pid) });
};

outbox.register('pin.set', {
  done: (e) => reload(e.projectId),
  fail: (e, err) => {
    reload(e.projectId);
    toast(t('sync.failed', { message: err instanceof Error ? err.message : t('task.failed') }), { kind: 'error' });
  },
});

export function setItemPinned(projectId: string, kind: PinKind, targetId: string, title: string, on: boolean) {
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'pin.set', projectId, method: 'PUT', url: `/api/projects/${projectId}/pins`, body: { opId, kind, targetId, on }, overlay: { type: 'pin', kind, targetId, on } })
    .catch(() => {});
  pinnedFeedback(projectId, title, on);
}

/** AF11: „An Übersicht angeheftet“ mit dem Weg dorthin; Lösen geht über denselben Menüeintrag am Inhalt */
export function pinnedFeedback(projectId: string, title: string, on: boolean) {
  if (!on) return void toast(savedNote(t('pin.undone', { title })));
  toast(savedNote(t('pin.done', { title })), {
    action: {
      label: t('pin.toOverview'),
      run: () => void import('../../app/router.js').then(({ router }) => router.navigate({ to: '/p/$projectId/uebersicht', params: { projectId } })),
    },
  });
}

/** Ist ein Inhalt angeheftet? Serverstand plus offene Änderungen */
export function usePinned(projectId: string, enabled = true): (kind: PinKind, id: string) => boolean {
  const q = usePins(projectId, enabled);
  const pending = useOutboxEntries(outbox, projectId);
  return useMemo(() => {
    const set = new Set((q.data?.items ?? []).map((x) => `${x.kind}:${x.targetId}`));
    for (const e of pending) {
      const o = e.overlay;
      if (o?.type !== 'pin') continue;
      if (o.on) set.add(`${o.kind}:${o.targetId}`);
      else set.delete(`${o.kind}:${o.targetId}`);
    }
    return (kind: PinKind, id: string) => set.has(`${kind}:${id}`);
  }, [q.data, pending]);
}
